import { useRef, useState } from "react";
import { Pin, PinOff, GripVertical } from "lucide-react";

/* Small pin toggle shown on hover of a sidebar item (always visible once pinned). */
export const PinBtn = ({ on, onClick, testid }) => (
  <span role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onClick(e); } }}
    title={on ? "Unpin" : "Pin to top"} data-testid={testid}
    className={`ml-auto rounded p-0.5 transition-opacity ${on ? "opacity-100 text-amber-500" : "opacity-0 max-lg:opacity-40 group-hover:opacity-60 hover:!opacity-100"}`}>
    {on ? <Pin className="h-3.5 w-3.5 fill-current" /> : <Pin className="h-3.5 w-3.5" />}
  </span>
);

/* "Pinned" group at the top of the sidebar — saved per user (ui_prefs.sidebar_pins).
   Drag the grip (mouse or finger) to reorder; order is saved on release. */
export const PinnedGroup = ({ pins, pool, show, activePanel, bind, onOpen, onUnpin, onReorder }) => {
  const [order, setOrder] = useState(null);  // live order while dragging
  const drag = useRef(null);
  const ids = (order || pins).filter(id => pool.some(i => i.id === id));
  const items = ids.map(id => pool.find(i => i.id === id));
  if (!items.length) return null;

  const start = (id, e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = id;
    setOrder(pins);
  };
  const move = (e) => {
    if (!drag.current) return;
    const over = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-pin-id]")?.dataset.pinId;
    if (!over || over === drag.current) return;
    setOrder(prev => {
      const list = [...(prev || pins)];
      const from = list.indexOf(drag.current), to = list.indexOf(over);
      if (from < 0 || to < 0) return prev;
      list.splice(from, 1); list.splice(to, 0, drag.current);
      return list;
    });
  };
  const end = () => {
    if (!drag.current) return;
    drag.current = null;
    const next = order;
    setOrder(null);
    if (next && next.join() !== pins.join()) onReorder(next);
  };

  return (
    <div className="mb-3 space-y-1 border-b border-slate-200 pb-3" data-testid="nav-pinned-group">
      {show && <p className="mb-2 flex items-center gap-1 px-3 text-xs font-semibold uppercase tracking-wider text-amber-500"><Pin className="h-3 w-3" /> Pinned
        {items.length > 1 && <span className="ml-auto font-normal normal-case tracking-normal text-[10px] text-slate-400">drag ⋮⋮ to reorder</span>}</p>}
      {items.map(item => (
        <button key={`pin-${item.id}`} type="button" onClick={() => onOpen(item.id)} data-testid={`nav-pinned-${item.id}`} data-pin-id={item.id} {...bind(item.label)}
          className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-all ${order && drag.current === item.id ? "ring-2 ring-amber-400 bg-amber-50 text-slate-800" : activePanel === item.id ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md" : "text-slate-600 hover:bg-amber-50"}`}>
          {show && items.length > 1 && (
            <span title="Drag to reorder" data-testid={`pin-drag-${item.id}`}
              onPointerDown={(e) => start(item.id, e)} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
              onClick={(e) => e.stopPropagation()}
              className="-ml-2 cursor-grab touch-none rounded p-0.5 opacity-40 hover:opacity-100 active:cursor-grabbing max-lg:opacity-70">
              <GripVertical className="h-4 w-4" /></span>)}
          <item.icon className="h-5 w-5 flex-shrink-0" />
          {show && <span className="flex-1 truncate text-left font-medium">{item.label}</span>}
          {show && (
            <span role="button" tabIndex={0} title="Unpin" data-testid={`unpin-${item.id}`} onClick={(e) => onUnpin(item.id, e)}
              className="rounded p-0.5 opacity-0 group-hover:opacity-80 hover:!opacity-100 max-lg:opacity-60"><PinOff className="h-3.5 w-3.5" /></span>)}
        </button>))}
    </div>
  );
};
